import { useState, useRef, useEffect } from 'react';
import styled, { useTheme } from 'styled-components';
import Button from '../../../../components/Button';
import TextInput from '../../../../components/Inputs/Text';
import VerificationCodeInput from '../../../../components/VerificationCodeInput';
import {
  useGiftForm,
  handleKeyboardNext,
  handleKeyboardDone,
} from '../../../../components/GiftFormContext';
import { Controller } from 'react-hook-form';
import { useLoginWithEmail } from '@privy-io/react-auth';
import { toast } from 'react-toastify';
import { usePrivy } from '@privy-io/react-auth';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import {
  EVENT_GIFT_FLOW_SENDER_CODE_FAILED,
  EVENT_GIFT_FLOW_SENDER_CODE_SENT,
  EVENT_GIFT_FLOW_SENDER_CODE_VERIFIED,
  EVENT_GIFT_FLOW_SENDER_EMAIL_CHANGED,
  EVENT_GIFT_FLOW_SENDER_NAME_CHANGED,
  sendEvent,
} from '../../../../common/analytics';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.text.secondary};
`;

const EmailSubtitle = styled(Subtitle)`
  margin-bottom: 8px;
`;

const SenderInfo = ({
  onContinue,
  buttonLabel = 'Continue',
  isModal,
  currentStep,
}) => {
  const [status, setStatus] = useState('awaiting-input');
  const { sendCode, state, loginWithCode } = useLoginWithEmail();
  const { user, ready } = usePrivy();
  const { setValue, senderName, showFreeGift } = useGiftForm();
  const otpRef = useRef(null);

  // Refs for keyboard navigation
  const senderNameRef = useRef(null);
  const emailRef = useRef(null);

  const emailSchema = yup.object({
    senderName: yup
      .string()
      .optional()
      .max(32, 'From name must be at most 32 characters'),
    email: yup.string().email('Invalid email').required('Email is required'),
  });

  // Get user email if already logged in
  const userEmail = user?.email?.address || '';

  const theme = useTheme();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue: setFormValue,
  } = useForm({
    resolver: yupResolver(emailSchema),
    defaultValues: {
      senderName: senderName || '',
      email: userEmail || '',
    },
  });

  const email = watch('email');
  const formSenderName = watch('senderName');

  // Pre-fill email if user is already logged in
  useEffect(() => {
    if (ready && userEmail && !email) {
      setFormValue('email', userEmail);
    }
  }, [ready, userEmail, email, setFormValue]);

  // Save sender name to form context when it changes
  useEffect(() => {
    if (formSenderName !== undefined) {
      setValue('senderName', formSenderName);
    }
  }, [formSenderName, setValue]);

  const handleSendCode = async (values) => {
    // If user is already logged in and email matches, skip verification
    if (ready && user && userEmail === values.email) {
      // User is already logged in with this email, no verification needed
      onContinue();
      return;
    }

    try {
      sendEvent(EVENT_GIFT_FLOW_SENDER_CODE_SENT, {
        is_free_gift: showFreeGift,
      });
      await sendCode({ email: values.email });
      setStatus('awaiting-code-input');
    } catch (error) {
      toast.error('Failed to send verification code. Please try again.');
    }
  };

  const handleLoginWithCode = async (code) => {
    try {
      await loginWithCode({ code });

      // Continue to next step
      sendEvent(EVENT_GIFT_FLOW_SENDER_CODE_VERIFIED, {
        is_free_gift: showFreeGift,
      });
      onContinue();
    } catch (error) {
      sendEvent(EVENT_GIFT_FLOW_SENDER_CODE_FAILED, {
        is_free_gift: showFreeGift,
      });
      toast.error('Invalid code');
      if (otpRef.current) {
        otpRef.current.clear();
      }
    }
  };

  if (status === 'awaiting-code-input') {
    return (
      <Container>
        <div>
          <h2
            style={{
              fontSize: '18px',
              fontWeight: 600,
              marginBottom: '8px',
              color: theme.text.title,
            }}
          >
            Verification code
          </h2>
          <Subtitle>
            Please check {email} for an email from privy.io and enter your code
            below.
          </Subtitle>
        </div>
        <VerificationCodeInput
          ref={otpRef}
          length={6}
          loading={state?.status === 'submitting-code'}
          onComplete={(code) => handleLoginWithCode(code)}
          size='md'
          autoFocus
        />
        <Button
          fullWidth
          onClick={() => {
            setStatus('awaiting-input');
          }}
          style={{ marginTop: '8px' }}
        >
          Back
        </Button>
      </Container>
    );
  }

  return (
    <Container>
      <form onSubmit={handleSubmit(handleSendCode)}>
        <Controller
          name='senderName'
          control={control}
          render={({ field }) => (
            <div>
              <TextInput
                ref={(el) => {
                  field.ref(el);
                  senderNameRef.current = el;
                }}
                label='Sender'
                inputMode='text'
                type='text'
                placeholder='Your name (optional)'
                error={errors?.senderName?.message}
                enterkeyhint='next'
                onKeyDown={(e) => handleKeyboardNext(e, emailRef)}
                value={field.value}
                onChange={(e) => {
                  field.onChange(e);
                  sendEvent(EVENT_GIFT_FLOW_SENDER_NAME_CHANGED, {
                    senderName: e.target.value,
                    is_free_gift: showFreeGift,
                  });
                }}
                onBlur={field.onBlur}
                name={field.name}
              />
            </div>
          )}
        />
        <div style={{ marginTop: '8px' }}>
          <Controller
            name='email'
            control={control}
            render={({ field }) => (
              <div>
                <TextInput
                  ref={(el) => {
                    field.ref(el);
                    emailRef.current = el;
                  }}
                  label='Email'
                  inputMode='email'
                  type='email'
                  placeholder='your@email.com'
                  error={errors?.email?.message}
                  enterkeyhint='done'
                  onKeyDown={(e) =>
                    handleKeyboardDone(e, emailRef, {
                      onProceed: () => {
                        // Submit the form when Enter is pressed on email field
                        handleSubmit(handleSendCode)();
                      },
                      isValidForm: isValid,
                    })
                  }
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e);
                    sendEvent(EVENT_GIFT_FLOW_SENDER_EMAIL_CHANGED, {
                      email: e.target.value,
                      is_free_gift: showFreeGift,
                    });
                  }}
                  onBlur={field.onBlur}
                  name={field.name}
                />
                <EmailSubtitle>
                  (We'll send your receipt and gift updates here)
                </EmailSubtitle>
              </div>
            )}
          />
        </div>
        <Button
          fullWidth
          clickAnimation='bouncy'
          type='submit'
          loading={state?.status === 'sending-code'}
          disabled={!isValid}
        >
          {buttonLabel}
        </Button>
      </form>
    </Container>
  );
};

export default SenderInfo;
